import type {
  WorkflowPresenceEntry,
  WorkflowPresenceParticipant,
  WorkflowPresenceState,
} from '@affine/core/modules/editor/services/workspace-realtime.service';
import { WorkspaceRealtimeService } from '@affine/core/modules/editor/services/workspace-realtime.service';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { LiveData, Service } from '@toeverything/infra';
import type { Subscription } from 'rxjs';

export const WORKFLOW_LIVE_COLLABORATION_FLAG =
  'enable_workflow_live_collaboration';

export type {
  WorkflowPresenceEntry,
  WorkflowPresenceParticipant,
  WorkflowPresenceState,
};

export type WorkflowPresenceVersionKey =
  | 'draft'
  | `published:${number}`
  | `snapshot:${number}`;

export function isWorkflowPresenceVersionKey(
  value: string
): value is WorkflowPresenceVersionKey {
  return (
    value === 'draft' ||
    /^published:\d+$/.test(value) ||
    /^snapshot:\d+$/.test(value)
  );
}

export function versionPresenceKey(
  workflowId: string,
  versionKey: WorkflowPresenceVersionKey
): string {
  return `${workflowId}\u001f${versionKey}`;
}

export function compareWorkflowPresenceParticipants(
  a: WorkflowPresenceParticipant,
  b: WorkflowPresenceParticipant,
  localUserId: string | null = null
): number {
  const rank = (participant: WorkflowPresenceParticipant): number => {
    if (participant.state.focus === 'moderating') return 0;
    if (localUserId && participant.userId === localUserId) return 1;
    if (participant.state.focus === 'following') return 2;
    return 3;
  };
  const rankDiff = rank(a) - rank(b);
  if (rankDiff !== 0) return rankDiff;
  const updated = b.updatedAt - a.updatedAt;
  if (updated !== 0) return updated;
  const name = (a.userName ?? '').localeCompare(b.userName ?? '');
  if (name !== 0) return name;
  const user = a.userId.localeCompare(b.userId);
  return user === 0 ? a.clientId.localeCompare(b.clientId) : user;
}

export function getWorkflowPresenceDisplayName(
  participant: Pick<WorkflowPresenceParticipant, 'userId' | 'userName'>
): string {
  const userName = participant.userName?.trim();
  return userName || participant.userId || 'Unknown';
}

export function sortWorkflowPresenceParticipants(
  participants: WorkflowPresenceParticipant[],
  localUserId: string | null = null
): WorkflowPresenceParticipant[] {
  return [...participants].sort((a, b) =>
    compareWorkflowPresenceParticipants(a, b, localUserId)
  );
}

export class WorkflowLivePresenceService extends Service {
  private readonly watchedWorkflowIds = new Set<string>();
  private readonly activeTargets = new Set<string>();
  private readonly targetStates = new Map<string, WorkflowPresenceState>();
  private readonly flagSubscription: Subscription;

  readonly enabled$ =
    this.featureFlagService.flags.enable_workflow_live_collaboration.$;

  readonly presence$ = LiveData.computed<WorkflowPresenceEntry[]>(get => {
    if (!get(this.enabled$)) return [];
    return get(this.realtimeService.workflowPresence$);
  });

  readonly participantsByWorkflow$ = this.presence$.map(entries => {
    const grouped = new Map<string, WorkflowPresenceParticipant[]>();
    for (const entry of entries) {
      const current = grouped.get(entry.workflowId) ?? [];
      current.push(...entry.participants);
      grouped.set(entry.workflowId, current);
    }
    const localUserId = this.getCurrentUserId();
    return new Map(
      Array.from(grouped.entries()).map(([workflowId, participants]) => [
        workflowId,
        sortWorkflowPresenceParticipants(participants, localUserId),
      ])
    );
  });

  readonly participantsByVersion$ = this.presence$.map(entries => {
    const grouped = new Map<string, WorkflowPresenceParticipant[]>();
    const localUserId = this.getCurrentUserId();
    for (const entry of entries) {
      if (!isWorkflowPresenceVersionKey(entry.versionKey)) continue;
      grouped.set(
        versionPresenceKey(entry.workflowId, entry.versionKey),
        sortWorkflowPresenceParticipants(entry.participants, localUserId)
      );
    }
    return grouped;
  });

  constructor(
    private readonly realtimeService: WorkspaceRealtimeService,
    private readonly featureFlagService: FeatureFlagService
  ) {
    super();
    this.flagSubscription = this.enabled$.subscribe(enabled => {
      if (!enabled) {
        this.realtimeService.clearWorkflowPresenceLocalState();
        return;
      }
      void this.replayDesiredPresence();
    });
  }

  get enabled(): boolean {
    return this.enabled$.value === true;
  }

  getCurrentUserId(): string | null {
    return this.realtimeService.getCurrentUserId();
  }

  getCurrentClientId(): string | null {
    return this.realtimeService.getCurrentWorkflowPresenceClientId();
  }

  async watchWorkflowIds(workflowIds: string[]): Promise<void> {
    const normalized = this.normalizeWorkflowIds(workflowIds);
    if (normalized.length === 0) return;
    for (const workflowId of normalized) {
      this.watchedWorkflowIds.add(workflowId);
    }
    if (!this.enabled) return;
    await this.realtimeService.watchWorkflowPresence(normalized);
  }

  unwatchWorkflowIds(workflowIds: string[]): void {
    const normalized = this.normalizeWorkflowIds(workflowIds);
    if (normalized.length === 0) return;
    for (const workflowId of normalized) {
      this.watchedWorkflowIds.delete(workflowId);
    }
    if (!this.enabled) return;
    this.realtimeService.unwatchWorkflowPresence(normalized);
  }

  async joinWorkflowVersion(
    workflowId: string,
    versionKey: string,
    state: WorkflowPresenceState = {}
  ): Promise<void> {
    if (!isWorkflowPresenceVersionKey(versionKey)) return;
    const targetKey = versionPresenceKey(workflowId, versionKey);
    this.activeTargets.add(targetKey);
    const mergedState = this.mergeTargetState(targetKey, state);
    if (!this.enabled) return;
    await this.realtimeService.joinWorkflowPresence(
      workflowId,
      versionKey,
      mergedState
    );
  }

  updateWorkflowVersion(
    workflowId: string,
    versionKey: string,
    state: WorkflowPresenceState
  ): void {
    if (!isWorkflowPresenceVersionKey(versionKey)) return;
    const targetKey = versionPresenceKey(workflowId, versionKey);
    this.activeTargets.add(targetKey);
    const mergedState = this.mergeTargetState(targetKey, state);
    if (!this.enabled) return;
    this.realtimeService.updateWorkflowPresence(
      workflowId,
      versionKey,
      mergedState
    );
  }

  leaveWorkflowVersion(workflowId: string, versionKey: string): void {
    if (!isWorkflowPresenceVersionKey(versionKey)) return;
    const targetKey = versionPresenceKey(workflowId, versionKey);
    this.activeTargets.delete(targetKey);
    this.targetStates.delete(targetKey);
    if (!this.enabled) return;
    this.realtimeService.leaveWorkflowPresence(workflowId, versionKey);
  }

  private mergeTargetState(
    targetKey: string,
    patch: WorkflowPresenceState
  ): WorkflowPresenceState {
    const next = {
      ...this.targetStates.get(targetKey),
      ...patch,
    };
    this.targetStates.set(targetKey, next);
    return next;
  }

  private normalizeWorkflowIds(workflowIds: string[]): string[] {
    return Array.from(
      new Set(workflowIds.map(id => id.trim()).filter(Boolean))
    );
  }

  private reset({ emitNetwork }: { emitNetwork: boolean }): void {
    const watched = Array.from(this.watchedWorkflowIds);
    if (emitNetwork && watched.length > 0) {
      this.realtimeService.unwatchWorkflowPresence(watched);
    }
    for (const target of this.activeTargets) {
      const separator = target.indexOf('\u001f');
      if (separator < 0) continue;
      if (!emitNetwork) continue;
      this.realtimeService.leaveWorkflowPresence(
        target.slice(0, separator),
        target.slice(separator + 1)
      );
    }
    this.watchedWorkflowIds.clear();
    this.activeTargets.clear();
    this.targetStates.clear();
    if (!emitNetwork) {
      this.realtimeService.clearWorkflowPresenceLocalState();
    }
  }

  private async replayDesiredPresence(): Promise<void> {
    const watched = Array.from(this.watchedWorkflowIds);
    if (watched.length > 0) {
      await this.realtimeService.watchWorkflowPresence(watched);
    }
    for (const target of this.activeTargets) {
      const separator = target.indexOf('\u001f');
      if (separator < 0) continue;
      const workflowId = target.slice(0, separator);
      const versionKey = target.slice(separator + 1);
      if (!isWorkflowPresenceVersionKey(versionKey)) continue;
      await this.realtimeService.joinWorkflowPresence(
        workflowId,
        versionKey,
        this.targetStates.get(target) ?? {}
      );
    }
  }

  override dispose(): void {
    this.reset({ emitNetwork: this.enabled });
    this.flagSubscription.unsubscribe();
    super.dispose();
  }
}
